import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Modal, Alert, InputGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Breadcrumb from '../components/Breadcrumb';
import apiService from '../services/apiService';
import Swal from 'sweetalert2';
import './test.css';

const ManageTransactionCoordinators = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createForm, setCreateForm] = useState({ name: '', base_rate: 1 }); // percentage input (e.g., 1 = 1%)
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', base_rate: 0 });
  const [savingEdit, setSavingEdit] = useState(false);

  const breadcrumbItems = [
      { label: 'Commissions', href: null},
      { label: 'Management', href: null},
      { label: 'Transaction Coordinator', href: null }
  ];

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTransactionCoordinatorsManage();
      setItems(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      setError(e.message || 'Failed to load transaction coordinators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Helper: format percent from decimal (0.05 => 5%)
  const formatPercent = (val) => {
    if (val === null || val === undefined || val === '') return 'N/A';
    const num = Number(val);
    if (Number.isNaN(num)) return val;
    return `${(num * 100).toFixed(2)}%`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const payload = {
        name: createForm.name,
        // convert entered percentage to decimal for API
        base_rate: Number(createForm.base_rate) / 100,
      };
      await apiService.createTransactionCoordinator(payload);
      setCreateForm({ name: '', base_rate: 1 });
      setShowCreate(false);
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to create transaction coordinator');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '',
      // store percent in edit form for user-friendly input
      base_rate: typeof item.base_rate === 'number' ? (item.base_rate * 100) : 0,
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        // convert percent back to decimal for API
        base_rate: Number(editForm.base_rate) / 100,
      };
      await apiService.updateTransactionCoordinator(editItem.id, payload);
      setShowEdit(false);
      setEditItem(null);
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to update transaction coordinator');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: `Delete ${item.name}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await apiService.deleteTransactionCoordinator(item.id);
      await Swal.fire({ title: 'Deleted', text: `${item.name} removed successfully.`, icon: 'success', timer: 1200, showConfirmButton: false });
      await loadItems();
    } catch (e) {
      setError(e.message || 'Failed to delete transaction coordinator');
    }
  };

  return (
    <div className="py-3">
      <Breadcrumb items={breadcrumbItems} />

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">Transaction Coordinators</h2>
              <p className="page-subtitle text-muted mb-0">Manage coordinator records</p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                <Button
                  variant={showCreate ? 'primary' : 'outline-primary'}
                  className="d-flex align-items-center gap-2"
                  onClick={() => setShowCreate(true)}
                >
                  <i className="fas fa-plus"></i> New Coordinator
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            {/* <span className="text-muted small">Total: {items.length}</span> */}
          </div>
          {loading ? (
            <div className="text-muted small">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-muted small">No transaction coordinators available.</div>
          ) : (
            <div className="containesr py-2">
              <div className="row g-3">
                {items.map((tc, index) => (
                  <div className="col-md-6 col-lg-3" key={tc.id || index}>
                    <div className="agent-card p-3">
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="avatar-gradient">
                            <i className="fas fa-fw fa-user text-white fs-5"></i>
                          </div>
                          <div>
                            <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                              {tc.name}
                            </h5>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {/* Edit */}
                          <button
                            className="btn btn-light btn-sm edit-btn"
                            onClick={() => openEdit(tc)}
                            aria-label={`Edit ${tc.name}`}
                          >
                            <i className="fas fa-fw fa-pen"></i>
                          </button>
                        </div>
                      </div>

                      <hr className="my-2" />

                      {/* Body */}
                      <div className="card-body p-0">
                        <div className="commission-box">
                          <p className="small mb-1">Base Rate</p>
                          <p className="fw-bold fs-6 mb-0">
                            {typeof tc.base_rate === 'number' ? `${(tc.base_rate * 100).toFixed(2)}%` : tc.base_rate}
                          </p>
                        </div>

                        {/* Bottom actions */}
                        <div className="mt-3 d-flex justify-content-end">
                          <button
                            className="btn btn-danger btn-sm delete-btn"
                            onClick={() => handleDelete(tc)}
                            aria-label={`Delete ${tc.name}`}
                          >
                            <i className="fas fa-fw fa-trash"></i>
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* Create Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Transaction Coordinator</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreate} className="row g-3">
            <Form.Group as={Col} md={8} controlId="create_name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Enter name"
                required
              />
            </Form.Group>
            <Form.Group as={Col} md={4} controlId="create_base_rate">
              <Form.Label>Base Rate</Form.Label>
              <InputGroup>
                <InputGroup.Text>%</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={createForm.base_rate}
                  onChange={(e) => setCreateForm({ ...createForm, base_rate: e.target.value })}
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button type="submit" variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Transaction Coordinator</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="row g-3">
            <Form.Group as={Col} md={6} controlId="edit_name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Form.Group>
            <Form.Group as={Col} md={6} controlId="edit_base_rate">
              <Form.Label>Base Rate</Form.Label>
              <InputGroup>
                <InputGroup.Text>%</InputGroup.Text>
                <Form.Control type="number" step="0.01" min="0" max="100" value={editForm.base_rate} onChange={(e) => setEditForm({ ...editForm, base_rate: e.target.value })} />
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageTransactionCoordinators;